import React, { useEffect, useState } from "react";
import Header from "./components/Header";
import ContractCard from "./components/ContractCard";
import ContractEditor from "./components/ContractEditor";
import { Button, Loader } from "@thdinc/hpf-shared";
import { API_ROUTES } from "@/services/environment";
import { DELETE, GET, POST, PUT } from "@/services/api";
import { useAppSelector } from "@/hooks/hook";
import Alert from "@/components/alert/Alert";
import ChangesAlert from "@/components/alert/ChangesAlert";

const { contract, getAllContracts } = API_ROUTES;

const ContractsPage = () => {
    const { refreshToken, business_id } = useAppSelector((s) => s.user);

    const [contracts, setContracts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);

    const [contractLoadingId, setContractLoadingId] = useState<string | null>(null);

    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [selected, setSelected] = useState<any>(null);
    const [contractToDelete, setContractToDelete] = useState<any>(null);

    // -----------------------------
    // Helpers
    // -----------------------------
    const resetForm = () => {
        setSelected(null);
        setTitle("");
        setBody("");
    };

    const showError = (error: any, fallback: string) => {
        Alert({
            type: "error",
            message: error?.response?.data?.message || fallback,
        });
    };

    const isBodyEmpty = (body: string) => {
        return !body
            .replace(/&nbsp;/gi, ' ') // convert HTML space to real space
            .trim();
    };

    // -----------------------------
    // Fetch Contracts
    // -----------------------------
    const fetchContracts = async () => {
        try {
            setListLoading(true);

            const res = await GET(`${getAllContracts}?businessId=${business_id}`, refreshToken);

            const rows = res?.data?.rows || [];
            const sorted = [...rows].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
            setContracts(sorted);
        } catch (err) {
            showError(err, "Failed to load contracts");
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchContracts();
    }, []);

    // -----------------------------
    // Create OR Update Contract
    // -----------------------------
    const saveContract = async () => {
        if (!title.trim())
            return Alert({ type: "error", message: "Title is required" });
        if (isBodyEmpty(body))
            return Alert({ type: "error", message: "Body is required" });

        const titleLower = title.trim().toLowerCase();
        const hasDuplicate = contracts.some(
            (c) => c.id !== selected?.id && c.title?.trim().toLowerCase() === titleLower
        );
        if (hasDuplicate)
            return Alert({ type: "error", message: "A contract with this name already exists" });

        try {
            setLoading(true);

            const payload = { title, body };

            if (selected) {
                await PUT(`${contract}/${selected.id}`, payload, refreshToken);
                Alert({ message: "Contract updated successfully" });
            } else {
                await POST(
                    contract,
                    { business_id, ...payload, is_default: false },
                    refreshToken
                );
                Alert({ message: "Contract created successfully" });
            }

            resetForm();
            fetchContracts();
        } catch (err) {
            showError(err, "Save failed");
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------
    // Delete Contract
    // -----------------------------
    const deleteContract = async (id: string) => {
        try {
            setContractLoadingId(id);
            await DELETE(`${contract}/${id}`, refreshToken);

            Alert({ message: "Contract deleted" });
            fetchContracts();
        } catch (err) {
            showError(err, "Delete failed");
        } finally {
            setContractLoadingId(null);
        }
    };

    // -----------------------------
    // Set Default Contract
    // -----------------------------
    const setDefaultContract = async (id: string) => {
        try {
            setContractLoadingId(id);

            await PUT(`${contract}/${id}`, { is_default: true }, refreshToken);

            Alert({ message: "Default contract updated" });
            fetchContracts();
        } catch (err) {
            showError(err, "Failed to set default");
        } finally {
            setContractLoadingId(null);
        }
    };

    // -----------------------------
    // Select Contract
    // -----------------------------
    const handleEdit = (item: any) => {
        setSelected(item);
        setTitle(item.title);
        setBody(item.body);
    };

    return (
        <div>
            <Header hideInvoice={true} />

            {/* Contract List */}
            <div className="mt-10 max-w-6xl">
                {listLoading ? (
                    <Loader color="bg-red-500" />
                ) : (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {contracts.map((c) => (
                            <ContractCard
                                key={c.id}
                                title={c.title}
                                isDefault={c.is_default}
                                isSelected={selected?.id === c.id}
                                isLoading={contractLoadingId === c.id}
                                onEdit={() => handleEdit(c)}
                                onDelete={() => setContractToDelete(c)}
                                onSetDefault={() => setDefaultContract(c.id)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Editor */}
            <div className="bg-white mt-6 pb-6 px-3 rounded-xl">
                <div className="pt-6 my-2">
                    <p className="text-[12px] font-[500] mb-2 pl-2">
                        Contract Title <span className="text-primary-red">*</span>
                    </p>

                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={64}
                        placeholder="Title"
                        className="w-[310px] px-4 py-2 h-[40px] bg-p-gray rounded-full text-sm"
                    />
                </div>

                <ContractEditor html={body} setHtml={setBody} />
                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    {selected && (
                        <Button
                            type="button"
                            classes="bg-black text-white px-4 py-2"
                            onClick={resetForm}
                        >
                            Cancel
                        </Button>
                    )}

                    <Button
                        type="button"
                        classes="bg-primary-red text-white px-4 py-2"
                        onClick={saveContract}>
                        {loading ? (
                            <Loader color="bg-gray-50" />
                        ) : selected ? (
                            "Update Contract"
                        ) : (
                            "Save Contract"
                        )}
                    </Button>
                </div>
            </div>

            <ChangesAlert
                title="Delete Contract"
                desc={`Are you sure you want to delete "${contractToDelete?.title || "this contract"}"? This action cannot be undone.`}
                positiveButtonText="Delete"
                negativeButtonText="Cancel"
                isOpen={!!contractToDelete}
                isLoading={contractLoadingId === contractToDelete?.id}
                handleSaveChanges={async () => {
                    await deleteContract(contractToDelete.id);
                    setContractToDelete(null);
                }}
                handleDiscardChanges={() => setContractToDelete(null)}
            />
        </div>
    );
};

export default ContractsPage;
